/**
 * Layer-2 (Zod fetch-boundary) STRUCTURAL GUARDS for the Social Media app.
 *
 * Response-shape parity campaign — Phase 6b Layer-2 adoption (structural-guard
 * posture). Plan:
 * agents/plans/response_shape_fleet_rollout_plan_2026-06-09.md
 *
 * These guards are passed to `useApiQuery`'s `schema:` option (via
 * {@link asResponseGuard}) so the unwrapped, post-transform payload is validated
 * at the network boundary. A failure throws an attributable
 * `ResponseValidationError` carrying the request URL instead of surfacing as a
 * `Cannot read properties of undefined` / `x.map is not a function` crash deep
 * in a component render (the exact class the queries.ts envelope comments track).
 *
 * POSTURE — DELIBERATELY MAXIMALLY-LENIENT STRUCTURAL GUARDS (NOT full
 * `@plerity/contracts` schemas):
 *   - `objectResponseGuard`: `z.object({}).passthrough().nullable()` — asserts
 *     the unwrapped payload is an object OR `null`; **no field is asserted**
 *     (seo / email / booking / reviews / calls posture, Hard rules #2 + #4).
 *     Every field is preserved verbatim — the guard can never strip or reject a
 *     field the page renders.
 *   - `containerResponseGuard`: `z.union([object, array]).nullable()` — accepts
 *     an object OR an array OR `null`. Used for hooks whose static `<T>` is a
 *     bare array (`X[]`) because Social has DOCUMENTED `<T>`-vs-runtime envelope
 *     drift: several hooks were re-typed from `X[]` to `{ items: X[] }` after the
 *     runtime turned out to return the `{items}` OBJECT, not a bare array (see
 *     the envelope comments in `queries.ts`: usePosts, useCalendar, useInbox,
 *     useBrandProfileHistory, useArchiveJobs, useScrapeSchedules, useFormatPresets).
 *     A bare `arrayGuard` on a still-mis-typed hook would HARD-REJECT a working
 *     `{items}` response — exactly the Hard rule #1 anti-pattern. The union can
 *     never over-reject any real container response while still catching gross
 *     drift (a string / number / HTML error body where a container is expected).
 *
 * `.nullable()` is essential (Hard rules #1 + #2): Social endpoints emit
 * `{ success: true, data: null }` as a legitimate "no data" signal the UI
 * already degrades on (e.g. `useBrandProfile` returns `Partial<...> | null`).
 * A non-nullable guard would convert that soft-degradation into a hard
 * `ResponseValidationError`.
 *
 * These guards catch ONLY gross structural drift; they CANNOT over-reject a
 * response the UI renders today. No live response was captured this session
 * (the API requires a seeded, brand-scoped, Meta-backed tenant + JWT), so no
 * field-level / full-strictness `@plerity/contracts` schema is wired
 * (Hard rule #1). The authored `@plerity/contracts/social` OBJECT schemas
 * (PostItem / AccountItem / ClientBrand…) are the foundation a future
 * full-strictness session can adopt once live capture is feasible.
 *
 * The static `<T>` on each `useApiQuery` call stays owned by the hand-written
 * type in `../types/*` (Layer 1). {@link asResponseGuard} is the deliberate seam
 * casting these runtime guards to that static contract (Hard rule #3).
 */

import { z } from "zod";

/**
 * Cast a structural Zod guard to the hook's `ResponseValidator<T>` seam.
 *
 * Layer 1 (the hand-written `<T>`) owns the STATIC type; this guard owns RUNTIME
 * structure only. The lenient guard's inferred output intentionally differs from
 * `<T>`, so the cast bridges the two layers without weakening the caller's
 * static type (Hard rule #3).
 */
export function asResponseGuard<T>(schema: { parse(data: unknown): unknown }): {
  parse(data: unknown): T;
} {
  return schema as { parse(data: unknown): T };
}

/** Object-or-null payload guard — no field asserted (maximally lenient). */
const objectGuard = z.object({}).passthrough().nullable();

/** Object-OR-array-OR-null payload guard — for bare-array `<T>` with possible drift. */
const containerGuard = z.union([z.object({}).passthrough(), z.array(z.unknown())]).nullable();

/** Single-object response guard cast to the caller's static `<T>`. */
export function objectResponseGuard<T>(): { parse(data: unknown): T } {
  return asResponseGuard<T>(objectGuard);
}

/**
 * Container response guard (object | array | null) cast to the caller's static
 * `<T>`. Use for hooks whose `<T>` is a bare array but whose runtime container
 * shape is not backend-confirmed this session.
 */
export function containerResponseGuard<T>(): { parse(data: unknown): T } {
  return asResponseGuard<T>(containerGuard);
}
